import { useEffect, useState, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router';

import { facebookApiAdapter } from '@mssgme/lib';
import { useActions } from '@mssgme/hooks';
import { useIntegration } from '../useIntegration';

// const SCOPE = 'email,public_profile,pages_manage_metadata,pages_messaging,instagram_basic,instagram_manage_messages';
const SCOPE = 'email,public_profile,pages_manage_metadata,pages_messaging';
const FIELDS = 'accounts.limit(1000){access_token,id,name},name,id';

function useFacebookPages(fbApi) {
    const [isLoaded, setIsLoaded] = useState(false);
    const [pages, setPages] = useState([]);
    const [username, setUsername] = useState('');

    const loadPages = useCallback(() => {
        fbApi.api('/me', { fields: FIELDS }).then((response) => {
            setIsLoaded(true);
            setPages((response.accounts && response.accounts.data) || []);
            setUsername(response.name);
        });
    }, [fbApi]);

    const options = useMemo(() => pages.map(({ id, name }) => ({ value: id, label: name })), [pages]);

    return { loadPages, pages, options, username, isLoaded };
}

function useFacebookApi() {
    const [fbApi, setFbApi] = useState(null);
    const [plugError, setPlugError] = useState(null);

    useEffect(() => {
        facebookApiAdapter()
            .then((fbApi) => setFbApi(fbApi))
            .catch(() => {
                setPlugError('messengerIntegration.errors.plug_failed');
            });
    }, []);

    return { fbApi, plugError };
}

function useFacebookLogin(fbApi) {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const login = useCallback(async () => {
        const { status } = await fbApi.login({ scope: SCOPE });
        if (status === 'connected') {
            setIsLoggedIn(true);
        } else {
            throw new Error('Not connected');
        }
    }, [fbApi]);

    return { isLoggedIn, login };
}

export const useMessengerIntegration = ({ request }) => {
    const { api } = useActions();
    const { t, rootIntegrationsPath } = useIntegration();
    const history = useHistory();
    const [selectedPage, setSelectedPage] = useState(null);
    const { fbApi, plugError } = useFacebookApi();
    const { isLoggedIn, login } = useFacebookLogin(fbApi);
    const { isLoaded, pages, options, username, loadPages } = useFacebookPages(fbApi);
    const [errors, setErrors] = useState({});

    const handleLoginClick = async (e) => {
        e.preventDefault();
        try {
            await login();
            loadPages();
        } catch (e) {
            // skip
        }
    };

    const handleChange = (e) => {
        const value = e.target.value;
        const selectedPage = pages.find((item) => item.id === value);
        setSelectedPage(selectedPage);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            await request(
                api.integrations.create({ payload: { type: 'messenger', token: selectedPage.access_token, username } })
            );
            history.push(rootIntegrationsPath);
        } catch (e) {
            // errors handled by request()
        }
    };

    return {
        t,
        fbApi,
        plugError,
        handleSubmit,
        isLoggedIn,
        handleLoginClick,
        isLoaded,
        username,
        selectedPage,
        options,
        handleChange,
        errors,
    };
};
